import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as EmailValidator from 'email-validator';
import { ActionType, VerifyParam } from 'rc-slider-captcha';

import { PhoneCodePopup } from '../../components/Popups/PhoneCodePopup';
import { LoginTemplate } from '../../components/Templates';
import { RegionsCode } from '../../constants/region';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useRedirectHome from '../../hooks/useRedirectHome';
import useRegisterQuery from '../../hooks/useRegisterQuery';
import useSignIn from '../../hooks/useSignIn';
import { useSessionStore } from '../../stores/SessionStore';
import { useSystemStore } from '../../stores/SystemStore';
import verifyCaptcha from '../../utils/captcha';
import { getRegionFromPhoneCode } from '../../utils/region';

interface State {
  activeTab: 'email' | 'phone';
  email: string;
  phoneCode: string;
  phoneNumber: string;
  password: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
  captcha: number;
  showCaptcha: boolean;
  verified: boolean;
}

const { fetchCaptcha } = useSystemStore.getState();

interface LoginScreenProps {
  webPageType: WebPageType;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ webPageType }) => {
  const navigate = useNavigate();
  const { query } = useRegisterQuery();
  const { signIn } = useSignIn();
  const { sharedData } = useShareData();
  const { showPopup, hidePopup } = usePopup();

  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);

  useRedirectHome(isLoggedIn(), webPageType);

  const [state, setState] = useState<State>({
    activeTab: 'email',
    email: '',
    phoneCode: '+852',
    phoneNumber: '',
    password: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
    captcha: 0,
    showCaptcha: false,
    verified: false,
  });
  const captchaRef = useRef<number>(state.captcha);
  const onSelectTab = useCallback((tab: 'email' | 'phone') => {
    setState((s) => ({
      ...s,
      activeTab: tab,
      isClicked: false,
      errorMessage: '',
    }));
  }, []);
  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setState((s) => ({ ...s, email: event.target.value, errorMessage: '' }));
  }, []);
  const onPhoneNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        phoneNumber: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );
  const onPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        password: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );
  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);
  const onClickLogin = useCallback(async () => {
    if (state.verified) {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const isEmail = EmailValidator.validate(state.email);
      await signIn(
        {
          email:
            state.activeTab === 'email' && isEmail ? state.email : undefined,
          username:
            state.activeTab === 'email' && !isEmail ? state.email : undefined,
          phone:
            state.activeTab === 'phone'
              ? {
                  region:
                    getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
                  phoneNumber: state.phoneNumber,
                }
              : undefined,
          password: state.password,
          referralCode: sharedData?.referralCode,
        },
        onSetErrorMessage,
        sharedData,
      );
      setState((s) => ({
        ...s,
        isLoading: false,
        verified: false,
      }));
    } else {
      onSetErrorMessage('Please verify the captcha');
    }
  }, [
    onSetErrorMessage,
    sharedData,
    signIn,
    state.activeTab,
    state.email,
    state.password,
    state.phoneCode,
    state.phoneNumber,
    state.verified,
  ]);

  useEffect(() => {
    captchaRef.current = state.captcha;
  }, [state.captcha]);

  const onRequest = useCallback(async () => {
    const res = await fetchCaptcha();
    if (res) {
      setState((s) => ({ ...s, captcha: res.solution }));

      const convertToDataURL = (data: number[]): Promise<string> => {
        return new Promise((resolve, reject) => {
          const arrayBuffer = new Uint8Array(data).buffer;
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
          const reader = new FileReader();

          reader.onloadend = function () {
            resolve(reader.result as string);
          };

          reader.onerror = reject;

          reader.readAsDataURL(blob);
        });
      };

      const bgUrl = await convertToDataURL(res.data.background.data);
      const puzzleUrl = await convertToDataURL(res.data.slider.data);

      return { bgUrl, puzzleUrl };
    }
    return { bgUrl: '', puzzleUrl: '' };
  }, []);

  const onVerify = useCallback(async (data: VerifyParam) => {
    const res = await verifyCaptcha(captchaRef.current, {
      response: data.x,
      trail: data.trail,
    });
    if (res) {
      setState((s) => ({ ...s, verified: true, showCaptcha: false }));
    }
  }, []);

  const actionRef = useRef<ActionType>();

  const onClickCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: true }));
  }, []);

  useEffect(() => {
    if (state.showCaptcha) {
      actionRef.current?.refresh(true);
    }
  }, [state.showCaptcha]);

  const onCloseCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: false }));
  }, []);

  const onClickForget = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.ForgotPassword), {
      state: {
        activeTab: state.activeTab,
        email: state.email,
        phoneCode: state.phoneCode,
        phoneNumber: state.phoneNumber,
      },
    });
  }, [
    navigate,
    state.activeTab,
    state.email,
    state.phoneCode,
    state.phoneNumber,
    webPageType,
  ]);

  const onClickTryNow = useCallback(() => {
    navigate('/trial' + query);
  }, [navigate, query]);

  const onClickRegister = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.Register) + query);
  }, [navigate, query, webPageType]);

  const onClickPhoneCode = useCallback(() => {
    showPopup(
      <PhoneCodePopup
        phoneCode={state.phoneCode}
        onPrimary={hidePopup}
        onCode={(selectedCode) => {
          setState((prevState) => ({ ...prevState, phoneCode: selectedCode }));
          hidePopup();
        }}
        webPageType={webPageType}
      />,
    );
  }, [showPopup, state.phoneCode, hidePopup, webPageType]);

  return (
    <LoginTemplate
      webPageType={webPageType}
      activeTab={state.activeTab}
      onSelectTab={onSelectTab}
      email={{ value: state.email, onChange: onEmailChange }}
      phoneCode={state.phoneCode}
      phoneNumber={{ value: state.phoneNumber, onChange: onPhoneNumberChange }}
      password={{ value: state.password, onChange: onPasswordChange }}
      isClicked={state.isClicked}
      onClick={() => {
        onClickLogin().catch(console.error);
      }}
      errorMessage={state.errorMessage}
      isLoading={state.isLoading}
      onClickForget={onClickForget}
      showCaptcha={state.showCaptcha}
      onRequest={onRequest}
      onVerify={onVerify}
      actionRef={actionRef}
      onClickCaptcha={onClickCaptcha}
      onCloseCaptcha={onCloseCaptcha}
      verified={state.verified}
      onClickTryNow={onClickTryNow}
      onClickRegister={onClickRegister}
      onClickPhoneCode={onClickPhoneCode}
    />
  );
};

export default LoginScreen;
